import { parseMinorUnits, CURRENCIES, type CurrencyCode } from "@causetide/shared";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(
  method: string,
  url: string,
  body?: unknown,
  opts: { formData?: FormData; headers?: Record<string, string> } = {},
): Promise<T> {
  const res = await fetch(`/api/v1${url}`, {
    method,
    credentials: "include",
    headers: {
      ...(opts.formData ? {} : body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...opts.headers,
    },
    body: opts.formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
  });
  // the donor portal runs its own token auth — a 401 there must not sign out the staff app
  if (res.status === 401 && !url.startsWith("/auth/") && !url.startsWith("/portal/")) {
    window.dispatchEvent(new CustomEvent("causetide:unauthorized"));
  }
  const contentType = res.headers.get("content-type") ?? "";
  const data = contentType.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    const message = typeof data === "object" && data && "message" in data ? String((data as { message: unknown }).message) : res.statusText;
    throw new ApiError(res.status, message, data);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string, opts?: { headers?: Record<string, string> }) => request<T>("GET", url, undefined, opts),
  post: <T>(url: string, body?: unknown, opts?: { headers?: Record<string, string> }) => request<T>("POST", url, body, opts),
  postForm: <T>(url: string, formData: FormData) => request<T>("POST", url, undefined, { formData }),
  patch: <T>(url: string, body?: unknown, opts?: { headers?: Record<string, string> }) => request<T>("PATCH", url, body, opts),
  put: <T>(url: string, body?: unknown) => request<T>("PUT", url, body),
  delete: <T>(url: string) => request<T>("DELETE", url),
};

// ---- money: formatted in the org's base currency (set once after login) ----
let orgCurrency: CurrencyCode = "USD";
/** called by the Shell once the org loads so every fmtMoney call matches the org */
export const setMoneyCurrency = (code: string) => {
  if (code in CURRENCIES) orgCurrency = code as CurrencyCode;
};
export const getMoneyCurrency = () => orgCurrency;

const currencyInfo = (code?: string) => CURRENCIES[(code ?? orgCurrency) as CurrencyCode] ?? CURRENCIES.USD;

/** format minor units in an explicit currency (e.g. a gift's original tender) */
export const fmtMoneyIn = (code: string, minor: number | null | undefined) => {
  if (minor == null) return "—";
  const info = currencyInfo(code);
  const v = minor / 10 ** info.exponent;
  return `${info.symbol}${v.toLocaleString("en-US", { minimumFractionDigits: info.exponent, maximumFractionDigits: info.exponent })}`;
};

export const fmtMoney = (cents: number | null | undefined) => fmtMoneyIn(orgCurrency, cents);

export const fmtMoneyShort = (cents: number | null | undefined) => {
  if (cents == null) return "—";
  const info = currencyInfo();
  return `${info.symbol}${(cents / 10 ** info.exponent).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

export const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/** "2025-08" → "Aug 25" for chart axes and tooltips */
export const fmtMonth = (yyyyMm: string) => {
  const [y, m] = yyyyMm.split("-").map(Number);
  if (!y || !m) return yyyyMm;
  return `${new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short" })} ${String(y).slice(2)}`;
};

export const fmtDateTime = (iso: string | null | undefined) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

/** parse a user-typed amount into minor units of the given (or org) currency */
/** Minor units → plain editable decimal ("1234.50"), the inverse of parseMoney. */
export const moneyInputValue = (minor: number, code?: string): string => {
  const { exponent } = currencyInfo(code);
  return (minor / 10 ** exponent).toFixed(exponent);
};
export const parseMoney =(raw: string, code?: string): number | null => parseMinorUnits(raw, currencyInfo(code).exponent);

export const todayIso = () => new Date().toISOString().slice(0, 10);
